/**
 * EmployeeTableEditor - Table editor for human_capital_id node
 *
 * Displays and allows editing of employee data stored as JSON array.
 * Read-only table view with raw JSON editing capability.
 */

import { useState, useCallback } from 'react'
import { companyProfileApi, Employee } from '@services/companyProfileApi'

export interface EmployeeTableEditorProps {
  value: string
  onSave: (value: string) => void
  onCancel: () => void
  saving: boolean
}

export function EmployeeTableEditor({
  value,
  onSave,
  onCancel,
  saving
}: EmployeeTableEditorProps) {
  const [employees] = useState<Employee[]>(() =>
    companyProfileApi.parseEmployees(value)
  )
  const [editMode, setEditMode] = useState(false)
  const [rawJson, setRawJson] = useState(value || '[]')
  const [jsonError, setJsonError] = useState<string | null>(null)

  const handleSave = useCallback(() => {
    if (editMode) {
      // Validate JSON before saving
      try {
        JSON.parse(rawJson)
        setJsonError(null)
        onSave(rawJson)
      } catch (e) {
        setJsonError('Invalid JSON format')
      }
    } else {
      onSave(value)
    }
  }, [editMode, rawJson, value, onSave])

  const handleEditRaw = useCallback(() => {
    setEditMode(true)
    setRawJson(value || '[]')
  }, [value])

  const handleCancelEdit = useCallback(() => {
    setEditMode(false)
    setJsonError(null)
    setRawJson(value || '[]')
  }, [value])

  if (editMode) {
    return (
      <div className="editor-form">
        <div className="table-editor-info">
          Edit the raw JSON data below. Each employee should have: Full_name, Title,
          Process_primary, Process_secondary, LinkedIn_profile_URL
        </div>

        {jsonError && (
          <div className="node-editor-error">
            {jsonError}
          </div>
        )}

        <div className="editor-field">
          <label htmlFor="raw-json">Employee Data (JSON)</label>
          <textarea
            id="raw-json"
            value={rawJson}
            onChange={(e) => {
              setRawJson(e.target.value)
              setJsonError(null)
            }}
            rows={15}
            style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
          />
        </div>

        <div className="editor-buttons">
          <button
            type="button"
            className="editor-btn editor-btn-secondary"
            onClick={handleCancelEdit}
            disabled={saving}
          >
            Back to Table
          </button>
          <button
            type="button"
            className="editor-btn editor-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save JSON'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="table-editor">
      <div className="table-editor-info">
        Employee data is managed through the system. Use "Edit Raw JSON" for manual changes.
      </div>

      {employees.length === 0 ? (
        <div className="table-editor-empty">
          <p>No employee data available.</p>
          <p>Use "Edit Raw JSON" to add employee data manually.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table-editor-table">
            <thead>
              <tr>
                <th>Full Name</th>
                <th>Title</th>
                <th>Primary Process</th>
                <th>Secondary Process</th>
                <th>LinkedIn</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((emp, idx) => (
                <tr key={idx}>
                  <td>{emp.Full_name || '-'}</td>
                  <td>{emp.Title || '-'}</td>
                  <td>{emp.Process_primary || '-'}</td>
                  <td>{emp.Process_secondary || '-'}</td>
                  <td>
                    {emp.LinkedIn_profile_URL ? (
                      <a
                        href={emp.LinkedIn_profile_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        View
                      </a>
                    ) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="editor-buttons">
        <button
          type="button"
          className="editor-btn editor-btn-secondary"
          onClick={handleEditRaw}
          disabled={saving}
        >
          Edit Raw JSON
        </button>
        <button
          type="button"
          className="editor-btn editor-btn-secondary"
          onClick={onCancel}
          disabled={saving}
        >
          Close
        </button>
      </div>
    </div>
  )
}

export default EmployeeTableEditor
